// PieChartComponent.tsx
import React from "react";
import { Card, CardBody, CardHeader, Table } from "react-bootstrap";
import Chart from "react-google-charts";
import { Col, Row } from "reactstrap";

interface ChannelDetail {
  channelName: string;
  available: number;
  sold: number;
  reserved: number;
  allocated: number;
  cancelled: number;
  produced: number;
}

interface PieChartComponentProps {
  data: any[];
  channelDetails: ChannelDetail[];
  product: any;
}

const PieChartComponent: React.FC<PieChartComponentProps> = ({
  data,
  channelDetails,
  product,
}) => {
  // Calculate totals
  const totals = React.useMemo(() => {
    return channelDetails.reduce((acc, channel) => ({
      available: acc.available + channel.available,
      sold: acc.sold + channel.sold,
      reserved: acc.reserved + channel.reserved,
      allocated: acc.allocated + channel.allocated,
      cancelled: acc.cancelled + channel.cancelled,
      produced: acc.produced + channel.produced,
    }), {
      available: 0,
      sold: 0,
      reserved: 0,
      allocated: 0,
      cancelled: 0,
      produced: 0,
    });
  }, [channelDetails]);

  return (
    <Card className="mt-3">
      <CardHeader className="bg-white">
        <h4>Product Inventory Breakdown</h4>
      </CardHeader>
      <CardBody>
        <Row>
          <Col lg="5">
            <Chart
              chartType="PieChart"
              data={data}
              options={{
                title: "Inventory Distribution",
                pieHole: 0.4,
                slices: {
                  0: { color: "#5754F7" }, // Available
                  1: { color: "#36A2EB" }, // Sold
                  2: { color: "#FFCE56" }, // Reserved
                },
                legend: { position: 'labeled' },
              }}
              width="100%"
              height="300px"
            />
          </Col>
          <Col lg="7">
            {/* Channel-wise Details Table */}
            <h5 className="mb-3">Channel Details</h5>
            <div className="table-responsive">
              <Table bordered className="mb-4">
                <thead className="table-light">
                  <tr>
                    <th>Channel</th>
                    <th className="text-center">Available</th>
                    <th className="text-center">Sold</th>
                    <th className="text-center">Reserved</th>
                    <th className="text-center">Allocated</th>
                  </tr>
                </thead>
                <tbody>
                  {channelDetails.map((channel, index) => (
                    <tr key={index}>
                      <td className="fw-medium">{channel.channelName}</td>
                      <td className="text-center">
                        <span className={`fw-bold ${channel.available <= 0 ? "text-danger" : "text-success"}`}>
                          {channel.available}
                        </span>
                      </td>
                      <td className="text-center">
                        <span className="text-primary fw-medium">{channel.sold}</span>
                      </td>
                      <td className="text-center">
                        <span className="text-warning fw-medium">{channel.reserved}</span>
                      </td>
                      <td className="text-center">
                        <span className="text-info fw-medium">{channel.allocated}</span>
                      </td>
                    </tr>
                  ))}
                  
                  {/* Totals Row */}
                  <tr className="table-light">
                    <td className="fw-bold">Total</td>
                    <td className="text-center fw-bold">{totals.available}</td>
                    <td className="text-center fw-bold">{totals.sold}</td>
                    <td className="text-center fw-bold">{totals.reserved}</td>
                    <td className="text-center fw-bold">{totals.allocated}</td>
                  </tr>
                </tbody>
              </Table>
            </div>
          </Col>
        </Row>
      </CardBody>
    </Card>
  );
};

export default PieChartComponent;